import React, { useEffect, useCallback, useState, useRef } from 'react';
import styled from 'styled-components';
import ReactPlayer from 'react-player';
import { saveAs } from 'file-saver';
import { FiAlertTriangle, FiLoader } from 'react-icons/fi';
import VideoPlayer from '../../components/video/VideoPlayer';
import { loadImage, loadPdfFile, loadOtherFile } from '../../actions/resources';
import api from '../../actions/api';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3333';

const buildProxyUrl = (resource, suffix) => {
  if (!resource || !resource.file) {
    return '';
  }

  const baseName = resource.file.split('.')[0];
  const fileName = suffix ? suffix : `${baseName}.mpd`;
  return `${API_BASE_URL}/video-files/${resource.id}/${fileName}`;
};

function Preview(props) {
  const [previewResource, setPreviewResource] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const pollingIntervalRef = useRef(null);

  const getTypeMedia = useCallback(() => {
    const dispositive = window.navigator.platform;

    if (dispositive === 'iPhone') {
      return 'mp4';
    } else {
      return 'dash';
    }
  }, []);

  // Função para buscar recurso atualizado
  const fetchResource = useCallback(async resourceId => {
    try {
      const response = await api.get(`resources/${resourceId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching resource:', error);
      return null;
    }
  }, []);

  // Função para iniciar polling quando vídeo está processando
  const startPolling = useCallback(
    resourceId => {
      // Limpar intervalo anterior se existir
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      setIsProcessing(true);

      // Polling a cada 3 segundos
      pollingIntervalRef.current = setInterval(async () => {
        const updatedResource = await fetchResource(resourceId);

        if (
          updatedResource &&
          updatedResource.url &&
          updatedResource.url.trim() !== ''
        ) {
          // Vídeo processado! Parar polling e atualizar
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setIsProcessing(false);

          // Preparar recurso com URL atualizada
          const _type = getTypeMedia();
          let _url = '';
          const baseName = updatedResource.file
            ? updatedResource.file.split('.')[0]
            : '';
          const dashUrl = buildProxyUrl(updatedResource);
          const mp4Url =
            _type === 'mp4' && baseName
              ? buildProxyUrl(updatedResource, `${baseName}_720.mp4`)
              : '';
          _url = _type === 'mp4' ? mp4Url : dashUrl;

          setPreviewResource({
            url: _url,
            type: updatedResource.type,
            media_type: _type,
            extname: updatedResource.extname,
            name: updatedResource.name,
            id: updatedResource.id,
            progress_time_in_ms: updatedResource.progress_time_in_ms,
          });
        }
      }, 3000); // Polling a cada 3 segundos

      // Limpar polling após 10 minutos (timeout de segurança)
      setTimeout(() => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setIsProcessing(false);
        }
      }, 600000); // 10 minutos
    },
    [fetchResource, getTypeMedia]
  );

  const prepareResource = useCallback(
    async resource => {
      switch (resource.type) {
        case 'video': {
          setPreviewResource({});
          setIsProcessing(false);

          // Verificar se o vídeo está processando (URL vazia ou não definida)
          if (!resource.url || resource.url.trim() === '') {
            setIsProcessing(true);
            // Mostrar tipo video mesmo quando processando
            setPreviewResource({
              type: 'video',
              extname: resource.extname,
              name: resource.name,
              id: resource.id,
            });
            // Iniciar polling para verificar quando o processamento completar
            startPolling(resource.id);
            return;
          }

          setTimeout(() => {
            const _type = getTypeMedia();
            let _url = '';
            const baseName = resource.file ? resource.file.split('.')[0] : '';
            const dashUrl = buildProxyUrl(resource);
            const mp4Url =
              _type === 'mp4' && baseName
                ? buildProxyUrl(resource, `${baseName}_720.mp4`)
                : '';
            _url = _type === 'mp4' ? mp4Url : dashUrl;
            setPreviewResource({
              url: _url,
              type: resource.type,
              media_type: _type,
              extname: resource.extname,
              name: resource.name,
              id: resource.id,
              progress_time_in_ms: resource.progress_time_in_ms,
            });
          }, 100);

          break;
        }

        case 'image': {
          await loadImage(resource.id).then(image => {
            setPreviewResource({
              url: image,
              type: 'image',
              extname: resource.extname,
              name: resource.name,
            });
          });

          break;
        }

        case 'pdf': {
          loadPdfFile(resource.id).then(pdf => {
            setPreviewResource({
              url: pdf,
              type: 'pdf',
              extname: resource.extname,
              name: resource.name,
            });
          });

          break;
        }

        case 'application': {
          loadOtherFile(resource.id).then(file => {
            setPreviewResource({
              url: file,
              type: 'other',
              extname: resource.extname,
              name: resource.name,
            });
          });

          break;
        }

        case 'url': {
          setTimeout(() => {
            setPreviewResource({ url: resource.url, type: 'link' });
          }, 200);
          break;
        }

        case 'youtube': {
          setTimeout(() => {
            setPreviewResource({ url: resource.url, type: 'youtube' });
          }, 200);
          break;
        }

        default:
          setPreviewResource({ url: '', type: '', extname: '', name: '' });
      }
    },
    [startPolling]
  );

  const downloadFile = useCallback(file => {
    saveAs(file.url, `${file.name}`);
  }, []);

  useEffect(() => {
    if (props.resource.id) prepareResource(props.resource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.resource.id]);

  useEffect(() => {
    setPreviewResource({});
    setIsProcessing(false);

    // Limpar polling quando componente desmontar ou resource mudar
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [props.resource.id]);

  return (
    <Container className="">
      {(previewResource.type || isProcessing) && (
        <div className="col-12 text-center">
          {previewResource.type === 'image' && (
            <>
              <img
                src={previewResource.url}
                className="img-fluid"
                alt="Imagem"
                style={{ maxHeight: '500px' }}
              />
              {/* <div>{previewResource.name}</div> */}
            </>
          )}

          {previewResource.type === 'pdf' && (
            <object
              data={previewResource.url}
              type="application/pdf"
              style={{ height: '500px', width: '100%' }}
            >
              <iframe
                title={previewResource.name}
                src={previewResource.url}
                width="100%"
                height="100%"
              />
            </object>
          )}

          {previewResource.type === 'link' && (
            <div className="row justify-content-center">
              <URL className="col-md-4">
                <h4 className="mb-5 mt-4">Link Externo</h4>

                <div className="mb-5">
                  <h6>Deseja abrir esse link em uma noja janela?</h6>
                  <span className="text-warning">{previewResource.url}</span>
                </div>

                <a
                  target="_blank"
                  href={`${previewResource.url}`}
                  rel="noopener noreferrer"
                >
                  <button className="btn btn-lg btn-info">Abrir Link</button>
                </a>
              </URL>
            </div>
          )}

          {previewResource.type === 'other' && (
            <div className="row justify-content-center">
              <URL className="col-md-6">
                <div className="text-info">
                  <div>
                    <FiAlertTriangle size={100} />
                  </div>
                </div>

                <h5 className="mt-3">
                  Esse documento não pode ser visualizado.
                </h5>
                <h6>Deseja baixar esse documento?</h6>

                <div>
                  <span className="text-warning">{previewResource.name}</span>
                </div>

                <button
                  className="btn btn-lg btn-info mt-3"
                  onClick={() => downloadFile(previewResource)}
                >
                  Baixar Arquivo
                </button>
              </URL>
            </div>
          )}

          {previewResource.type === 'video' && !isProcessing && (
            <VideoContainer>
              <VideoPlayer
                src={previewResource.url}
                type={previewResource.media_type}
                resource_id={previewResource.id}
                progress_time_in_ms={previewResource.progress_time_in_ms}
                my_course_id={props.my_course_id}
              />
            </VideoContainer>
          )}

          {isProcessing && (
            <VideoContainer>
              <div className="text-center" style={{ padding: '60px 20px' }}>
                <FiLoader
                  size={60}
                  className="spinning"
                  style={{
                    animation: 'spin 1s linear infinite',
                    marginBottom: '20px',
                    color: '#007bff',
                  }}
                />
                <h5 className="mt-3">Processando vídeo...</h5>
                <p className="text-muted">
                  Aguarde enquanto o vídeo está sendo processado. Isso pode
                  levar alguns minutos.
                </p>
              </div>
            </VideoContainer>
          )}

          {previewResource.type === 'youtube' && (
            <VideoContainer className="col">
              <ReactPlayer url={previewResource.url} />
            </VideoContainer>
          )}
        </div>
      )}
    </Container>
  );
}

Preview.defaultProps = {
  resource: Object,
  my_course_id: String,
};

export default Preview;

export const Container = styled.div`
  /* max-height: 550px; */
  /* padding: 40px; */
  /* align-self: center; */
  /* background-color: #f9f9f9; */
`;
export const URL = styled.div`
  height: 300px;
  padding: 20px;
  background-color: #f9f9f9;
`;

export const VideoContainer = styled.div`
  width: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .spinning {
    animation: spin 1s linear infinite;
  }
`;
